import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Edit, Save, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../lib/api';
import { formatDate, formatDateTime } from '../../../lib/dateFormat';
import { personDetailPath } from '../../../lib/peopleRoutes';
import { relationshipOptionsFor, relationshipText } from '../../../lib/subprofileRelationships';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import TaskList from '../../../components/tasks/TaskList';
import { SubprofileContractCard, type SubprofileContract } from '../../../components/person/subprofile/SubprofileContractCard';
import KontaktwegeEditor from '../../../components/person/KontaktwegeEditor';
import KontaktwegeListe from '../../../components/person/KontaktwegeListe';
import type { Kontaktweg } from '../../../lib/api/stammdaten';

const TAB_VALUES = ['general', 'lessons', 'history'] as const;
type SubprofileTab = (typeof TAB_VALUES)[number];

interface Subprofile {
  id: string;
  index: number;
  name: string;
  salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma';
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  relationshipType?: string;
  communications?: Kontaktweg[];
  communicationChannels?: Kontaktweg[];
}

/**
 * Die Kontaktwege des Profils — beide Schreibweisen kommen vor, gepflegt wird
 * „communications" (T-0289).
 */
const kontaktwegeDes = (profil: Partial<Subprofile> | null): Kontaktweg[] => {
  if (Array.isArray(profil?.communications)) return profil.communications;
  if (Array.isArray(profil?.communicationChannels)) return profil.communicationChannels;
  return [];
};
interface ParentPerson {
  id: string;
  name: string;
}
interface HistoryEntry {
  id: number;
  type: string;
  description: string;
  details?: string | null;
  actorUserId?: number | null;
  actorEmail?: string | null;
  createdAt: string;
}

const SubprofileDetail = () => {
  const { id, subId } = useParams<{ id: string; subId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab: SubprofileTab = TAB_VALUES.includes(tabFromUrl as SubprofileTab)
    ? (tabFromUrl as SubprofileTab)
    : 'general';
  const [isLoading, setIsLoading] = useState(true);
  const [sub, setSub] = useState<Subprofile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Partial<Subprofile>>({});
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [newHist, setNewHist] = useState<{ type: string; description: string; details?: string }>(
    { type: 'note', description: '', details: '' }
  );
  const [managedBy, setManagedBy] = useState<ParentPerson | null>(null);
  const [contracts, setContracts] = useState<SubprofileContract[]>([]);
  const [activeTab, setActiveTab] = useState<SubprofileTab>(initialTab);

  const parseSubIndex = (sid?: string): number => {
    if (!sid) return 1;
    const parts = sid.split('-');
    const last = parts[parts.length - 1];
    const n = parseInt(last, 10);
    return Number.isNaN(n) ? 1 : n;
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await api.get<Subprofile>(`/api/personen/${id}/subprofiles/${subId}`);
        setSub(data);
        setForm({ ...data, communications: kontaktwegeDes(data) });
        try {
          const parent = await api.get<ParentPerson>(`/api/personen/${id}`);
          setManagedBy(parent);
        } catch {
          setManagedBy(null);
        }
        const [hist, contractRows] = await Promise.all([
          api.get<HistoryEntry[]>(`/api/personen/${id}/history?subIndex=${parseSubIndex(subId)}`),
          api.get<SubprofileContract[]>(`/api/personen/${id}/contracts?subprofileId=${encodeURIComponent(String(subId))}`),
        ]);
        const eintraege = hist as unknown;
        setHistory(
          Array.isArray(eintraege)
            ? (eintraege as HistoryEntry[])
            : Array.isArray((eintraege as { items?: unknown } | null)?.items)
              ? ((eintraege as { items: HistoryEntry[] }).items)
              : [],
        );
        setContracts(Array.isArray(contractRows) ? contractRows : []);
      } catch (e) {
        toast.error('Schülerprofil nicht gefunden');
        navigate(personDetailPath(String(id || ''), location.pathname));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, location.pathname, navigate, subId]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TAB_VALUES.includes(tab as SubprofileTab)) {
      setActiveTab(tab as SubprofileTab);
    }
  }, [searchParams]);

  const handleTabChange = (value: string) => {
    if (!TAB_VALUES.includes(value as SubprofileTab)) return;
    setActiveTab(value as SubprofileTab);
    setSearchParams({ tab: value }, { replace: true });
  };

  const activeContracts = contracts.filter((c) =>
    ['converted', 'accepted_by_student'].includes(String(c.enrollmentStatus || '').toLowerCase())
  );
  const nextContractPreview = activeContracts[0] ?? null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSelect = (name: string, value: string) =>
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // Passt die Beziehung nicht mehr zur Anrede, wird sie geleert (T-0306).
      if (
        name === 'salutation'
        && prev.relationshipType
        && !relationshipOptionsFor(value).some((o) => o.value === prev.relationshipType)
      ) {
        next.relationshipType = '';
      }
      return next;
    });

  const addHistory = async () => {
    if (!newHist.description.trim()) {
      toast.error('Beschreibung erforderlich');
      return;
    }
    try {
      const created = await api.post<HistoryEntry>(`/api/personen/${id}/history`, {
        type: newHist.type,
        description: newHist.description,
        details: newHist.details || undefined,
        subIndex: parseSubIndex(subId),
      });
      setHistory(prev => [created, ...prev]);
      setNewHist({ type: 'note', description: '', details: '' });
      toast.success('Historien-Eintrag erstellt');
    } catch (e) {
      toast.error('Historien-Eintrag fehlgeschlagen');
    }
  };

  const handleSave = async () => {
    if (!sub) return;
    setIsLoading(true);
    try {
      const updated = await api.put<Subprofile>(`/api/personen/${id}/subprofiles/${sub.id}`, {
        salutation: form.salutation,
        relationshipType: form.relationshipType,
        firstName: form.firstName,
        lastName: form.lastName,
        name: form.name,
        birthDate: form.birthDate,
        // Leere Zeilen aus dem Editor gehören nicht in den Datensatz.
        communications: kontaktwegeDes(form).filter((weg) => String(weg.value || '').trim() !== ''),
      });
      setSub(updated);
      setForm({ ...updated, communications: kontaktwegeDes(updated) });
      setIsEditing(false);
      toast.success('Schülerprofil gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !sub) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!sub) return null;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(personDetailPath(String(id || ''), location.pathname))}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zurück zur Person
        </Button>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => { setIsEditing(false); setForm({ ...sub, communications: kontaktwegeDes(sub) }); }}>
                Abbrechen
              </Button>
              <Button onClick={handleSave}>
                <Save className="mr-2 h-4 w-4" /> Speichern
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)}>
              <Edit className="mr-2 h-4 w-4" /> Bearbeiten
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant="outline" className="border-slate-400 bg-slate-100 text-slate-700">Schüler</Badge>
        <h1 className="text-2xl font-semibold">{sub.name}</h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="h-auto flex-wrap gap-1 rounded-full bg-muted p-1">
          <TabsTrigger value="general" className="rounded-full px-4">Allgemein</TabsTrigger>
          <TabsTrigger value="lessons" className="rounded-full px-4">Unterricht</TabsTrigger>
          <TabsTrigger value="history" className="rounded-full px-4">Historie</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Stammdaten</CardTitle>
              <CardDescription>Allgemeine Informationen des Schülerprofils</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">ID</label>
                  <div className="rounded-md border p-3 font-mono">{sub.id}</div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Anrede</label>
                  {isEditing ? (
                    <Select value={(form.salutation as string) ?? ''} onValueChange={(val) => handleSelect('salutation', val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Anrede wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Herr">Herr</SelectItem>
                        <SelectItem value="Frau">Frau</SelectItem>
                        <SelectItem value="Divers">Divers</SelectItem>
                        <SelectItem value="Firma">Firma</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="rounded-md border p-3">{sub.salutation || '-'}</div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Vorname</label>
                  {isEditing ? (
                    <Input name="firstName" value={form.firstName || ''} onChange={handleChange} />
                  ) : (
                    <div className="rounded-md border p-3">{sub.firstName || '-'}</div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nachname</label>
                  {isEditing ? (
                    <Input name="lastName" value={form.lastName || ''} onChange={handleChange} />
                  ) : (
                    <div className="rounded-md border p-3">{sub.lastName || '-'}</div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Geburtsdatum</label>
                  {isEditing ? (
                    <Input type="date" name="birthDate" value={form.birthDate || ''} onChange={handleChange} />
                  ) : (
                    <div className="rounded-md border p-3">{sub.birthDate ? formatDate(sub.birthDate) : '-'}</div>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Beziehung zur Hauptperson</label>
                  {isEditing ? (
                    <Select value={(form.relationshipType as string) || ''} onValueChange={(val) => handleSelect('relationshipType', val)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Beziehung wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {relationshipOptionsFor(form.salutation as string).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="rounded-md border p-3">{relationshipText(sub.relationshipType, managedBy?.name || 'Hauptprofil') || '-'}</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Kontaktwege</CardTitle>
              <CardDescription>
                Eigene E-Mail-Adressen und Nummern dieses Profils. Nachrichten aus den Assistenten gehen
                zusätzlich an diese Adressen; doppelte Adressen bekommen sie nur einmal.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <KontaktwegeEditor
                  idPrefix="profil-wege"
                  wege={kontaktwegeDes(form)}
                  onChange={(wege) => setForm((prev) => ({ ...prev, communications: wege }))}
                />
              ) : (
                <KontaktwegeListe
                  wege={kontaktwegeDes(sub)}
                  leer={`Keine eigenen Kontaktwege — Nachrichten gehen an ${managedBy?.name || 'das Kundenkonto'}.`}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Wird verwaltet von</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700">Kunde</Badge>
                  <span className="font-medium">{managedBy?.name || 'Hauptprofil'}</span>
                </div>
                <Button onClick={() => navigate(personDetailPath(String(id || ''), location.pathname))}>zur Person</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Nächster Unterricht</CardTitle>
              <CardDescription>Vorschau — Details unter Tab „Unterricht“</CardDescription>
            </CardHeader>
            <CardContent>
              {!nextContractPreview ? (
                <div className="rounded-lg border p-6 text-sm text-muted-foreground">Kein aktiver Unterricht</div>
              ) : (
                <SubprofileContractCard contract={nextContractPreview} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lessons" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {contracts.length === 0 ? (
              <Card className="md:col-span-2">
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  Noch keine Verträge für dieses Schülerprofil
                </CardContent>
              </Card>
            ) : (
              contracts.map((contract) => (
                <SubprofileContractCard key={contract.enrollmentId} contract={contract} />
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historie</CardTitle>
              <CardDescription>Chronik und Aufgaben zu diesem Schülerprofil</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-6 items-start">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Typ</label>
                  <Select value={newHist.type} onValueChange={(val) => setNewHist((prev) => ({ ...prev, type: val }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Typ wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Notiz</SelectItem>
                      <SelectItem value="call">Anruf</SelectItem>
                      <SelectItem value="email">E-Mail</SelectItem>
                      <SelectItem value="update">Update</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-4">
                  <label className="text-sm font-medium">Beschreibung</label>
                  <Input value={newHist.description} onChange={(e) => setNewHist((prev) => ({ ...prev, description: e.target.value }))} />
                </div>
                <div className="md:col-span-6">
                  <label className="text-sm font-medium">Details</label>
                  <textarea
                    className="w-full rounded-md border bg-transparent p-2 text-sm"
                    rows={4}
                    value={newHist.details || ''}
                    onChange={(e) => setNewHist((prev) => ({ ...prev, details: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-6 flex justify-end">
                  <Button onClick={addHistory}>
                    <PlusCircle className="mr-2 h-4 w-4" /> Eintrag hinzufügen
                  </Button>
                </div>
              </div>

              {history.length === 0 ? (
                <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">Noch keine Historie</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Zeitpunkt</th>
                        <th className="py-2 pr-4 font-medium">Bearbeiter</th>
                        <th className="py-2 font-medium">Ereignis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((h) => (
                        <tr key={h.id} className="border-b align-top">
                          <td className="py-3 pr-4 whitespace-nowrap text-muted-foreground">
                            {formatDateTime(h.createdAt)}
                          </td>
                          <td className="py-3 pr-4">
                            {h.actorEmail || (h.actorUserId ? `User #${h.actorUserId}` : '–')}
                          </td>
                          <td className="py-3">
                            <div className="font-medium">{h.description}</div>
                            <div className="text-xs uppercase tracking-wide text-muted-foreground">{h.type}</div>
                            {h.details ? (
                              <div className="mt-1 whitespace-pre-wrap text-muted-foreground">{h.details}</div>
                            ) : null}
                            <details className="mt-2">
                              <summary className="cursor-pointer text-xs text-muted-foreground">Aufgaben</summary>
                              <div className="mt-2">
                                <TaskList personHistoryId={Number(h.id)} showCreate={true} />
                              </div>
                            </details>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubprofileDetail;


